import SafeSessionStorage from './storage/SafeSessionStorage';

// WARNING: The value of INTERACTION_SESSION_ID_STORAGE_KEY will be used by Analytics Web Client to attach the session ID to events.
// If you change this, you will need to update the Analytics Web Client code as well.
const INTERACTION_SESSION_ID_STORAGE_KEY = 'interactionSession.id';
const INTERACTION_SESSION_ID_PREV_STORAGE_KEY = 'interactionSession.id.prev';
const INTERACTION_SESSION_EXPIRY_STORAGE_KEY = 'interactionSession.expiry';

export const DEFAULT_SESSION_EXPIRY_TIME_MS = 30 * 60 * 1000; // 30 minutes
export const DEFAULT_EXPIRY_DEBOUNCE = 5 * 1000; // 5 seconds
export const DEFAULT_DEBOUNCE_THRESHOLD_PERCENTAGE = 0.9; // 90% of expiry
export const NOOP = () => {};

const INTEGER_REGEX = /^\d+$/;

export type OptionsType = {
	providedSessionId?: string;
	// Analytics callback
	sendEventCallback?: Function;

	// AWC designed these to be customisable, but we're not reallly using them. Keeping them
	// to change as little as possible from the AWC implementation of SessionTracking
	sessionExpiryDebounce?: number;
	sessionExpiryDebounceThresholdPercentage?: number;
	sessionExpiryTime?: number;
};

export const INTERACTION_SESSION_ID_UPDATED_EVENT = 'interactionSessionIdUpdated';

/**
 * This class is essentially a wrapper around SessionStorage that introduces some failsafes (such as switching to InMemoryStorage if SessionStorage is not available)
 * and providing resilient methods for accessing and updating the interaction session ID.
 * This class can be initialised anywhere within a document, and `getCurrentInteractionSessionId` will always return the latest interaction session ID.
 *
 * The `updateSettings` method will overwrite the SessionStorage with the provided session ID, and is only intended to be used by the Analytics Web Client
 */
export default class InteractionSessionTracking {
	private safeSessionStorage: SafeSessionStorage;
	private sessionExpiryTime: number;
	private sessionExpiryDebounce: number;
	private sessionExpiryDebounceThresholdPercentage: number;
	private sessionExpiryDebounceTimer?: number;
	private analyticsReady: boolean = false;

	// This callback function will be called whenever a new session ID is generated
	// It expects a function that sends an operational analytics event, with
	// the first parameter being set to the action ('started' or 'renewed'), and the second parameter being
	// an InteractionSession object containing the new session ID and previous session ID.
	private sendEventCallback?: Function;

	constructor(options: OptionsType = {}) {
		this.sessionExpiryTime = options.sessionExpiryTime || DEFAULT_SESSION_EXPIRY_TIME_MS;
		this.sessionExpiryDebounce = options.sessionExpiryDebounce || DEFAULT_EXPIRY_DEBOUNCE;
		this.sessionExpiryDebounceThresholdPercentage =
			options.sessionExpiryDebounceThresholdPercentage || DEFAULT_DEBOUNCE_THRESHOLD_PERCENTAGE;

		options.sendEventCallback && this.setAnalyticsCallback(options.sendEventCallback);

		this.safeSessionStorage = new SafeSessionStorage();

		// Assign provided session ID if it is valid
		options.providedSessionId && this.setInteractionSessionId(options.providedSessionId);
	}

	getCurrentInteractionSessionId() {
		if (!this.analyticsReady) {
			return null;
		}
		const sessionId = this.safeSessionStorage.getItem(INTERACTION_SESSION_ID_STORAGE_KEY);
		const sessionExpiry =
			parseInt(
				this.safeSessionStorage.getItem(INTERACTION_SESSION_EXPIRY_STORAGE_KEY) || '0',
				10,
			) || 0;

		this.debouncedUpdateSessionExpiry(sessionExpiry);

		if (!sessionId || sessionExpiry <= Date.now() || Number.isNaN(sessionExpiry)) {
			return this.generateNewSessionId();
		}

		return sessionId;
	}

	getPreviousInteractionSessionId() {
		if (!this.analyticsReady) {
			return null;
		}
		return this.safeSessionStorage.getItem(INTERACTION_SESSION_ID_PREV_STORAGE_KEY);
	}

	updateSettings(options: OptionsType = {}) {
		// Only updating options we expect Analytics Web Client to set. This list may grow
		options.providedSessionId && this.setInteractionSessionId(options.providedSessionId);
		options.sendEventCallback && this.setAnalyticsCallback(options.sendEventCallback);
		options.sessionExpiryTime && this.setSessionExpiryTime(options.sessionExpiryTime);
	}

	private setInteractionSessionId(providedSessionId?: string) {
		if (this.validateProvidedSessionId(providedSessionId)) {
			this.generateNewSessionId(providedSessionId);
			this.debouncedUpdateSessionExpiry(0);
		}
	}

	private setAnalyticsCallback(sendEventCallback: Function) {
		if (typeof sendEventCallback === 'function') {
			this.sendEventCallback = sendEventCallback;
			this.analyticsReady = true;
		} else {
			this.sendEventCallback = NOOP;
			this.analyticsReady = false;
		}
	}

	private setSessionExpiryTime(sessionExpiryTime: number) {
		if (typeof sessionExpiryTime === 'number' && sessionExpiryTime > 0) {
			this.sessionExpiryTime = sessionExpiryTime;
			// Update the expiry time to reflect the new value
			this.debouncedUpdateSessionExpiry(0);
		}
	}

	private validateProvidedSessionId(providedSessionId?: string) {
		// Check if provided session ID can pass as valid Unix timestamp that is not in the future
		if (
			providedSessionId &&
			INTEGER_REGEX.test(providedSessionId) &&
			parseInt(providedSessionId, 10) <= Date.now()
		) {
			return true;
		} else {
			return false;
		}
	}

	private sendInteractionSessionEvent(action: string) {
		this.sendEventCallback &&
			this.sendEventCallback({
				action: action,
				actionSubject: 'interactionSession',
				source: 'unknown',
				tags: ['navigationExperiences'], // InteractionSession events will be registered under this platform product
			});
	}

	private generateNewSessionId = (providedSessionId?: string) => {
		const currentSessionId = this.safeSessionStorage.getItem(INTERACTION_SESSION_ID_STORAGE_KEY);
		const newSessionId = providedSessionId ? providedSessionId : Date.now().toString();

		this.safeSessionStorage.setItem(INTERACTION_SESSION_ID_STORAGE_KEY, newSessionId);

		// Store a copy of the current session ID so we can link sessions if needed
		if (currentSessionId) {
			this.safeSessionStorage.setItem(INTERACTION_SESSION_ID_PREV_STORAGE_KEY, currentSessionId);

			// Execute new session renewed callback
			this.sendInteractionSessionEvent('renewed');
		} else {
			// Execute new session callback
			this.sendInteractionSessionEvent('started');
		}

		// Event listener is in useCrossProductUrl.tsx to update URL query params
		document.dispatchEvent(new Event(INTERACTION_SESSION_ID_UPDATED_EVENT));

		return newSessionId;
	};

	private updateSessionExpiry = () => {
		const expiry = Date.now() + this.sessionExpiryTime;
		this.safeSessionStorage.setItem(INTERACTION_SESSION_EXPIRY_STORAGE_KEY, expiry.toString());
		return expiry;
	};

	private debouncedUpdateSessionExpiry = (currentExpiry: number) => {
		const { updateSessionExpiry } = this;
		// If the current expiry is out of date by more than 10% of the expiry, update immediately
		if (
			currentExpiry <
			Date.now() + this.sessionExpiryDebounceThresholdPercentage * this.sessionExpiryTime
		) {
			updateSessionExpiry();
		} else {
			if (this.sessionExpiryDebounceTimer) {
				clearTimeout(this.sessionExpiryDebounceTimer);
			}
			// @ts-ignore Something wrong with our types... this is legit
			this.sessionExpiryDebounceTimer = setTimeout(() => {
				updateSessionExpiry();
			}, this.sessionExpiryDebounce);
		}
	};
}
